import { userInteractionButtons, BUTTON_TYPE } from './userinteraction.config';
import { USER_INTERACTION_TYPE } from '../../components/events/events.config.js';
import { differenceInMilliseconds, intervalToDuration } from 'date-fns';
import * as utils from '../utils/utils.js';
import * as userPerms from '../utils/userPerms.js';
import { addToCalendarData, ieDownloadCalendar } from '../utils/add-to-calendar.js';
import * as videojs from 'video.js';
import { VIEWING_STATE } from '../events/events.config.js';
import { Chart } from 'chart.js/auto';
import isoCountries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

const videoJS = videojs.default;

export default class UserInteraction {
    constructor($interval, $sce, watchService) {
        this.$interval = $interval;
        this.$sce = $sce;
        this.watchService = watchService;
        this.event = this.watchService.event;
        this.user = utils.getCurrentUser();

        this.viewEventUrl = utils.GetViewEventRelativeUrl(this.event);

        this.qnaModerationUrl = utils.GetQnARelativeUrl(this.event);

        this.buttons = userInteractionButtons;
        this.BUTTON_TYPE = BUTTON_TYPE;
        this.imageVer = siteSettings.imageVersion;

        this.displayCorrectButtons();
        this.customizeBtnOnClick();

        this.slidoUrl = '';
        this.configureSlido();

        this.unreadComments = false;
        this.unreadAnnouncements = false;
        this.unreadAnswers = false;
        this.firstLoad = true;

        this.announcementsAttribute = this.announcementsOnly() ? 'yes' : 'no';

        this.commentPageSize = 3;
        this.maxNumToolbarButtons = 0;
        this.moreMenuStartIndex = -1;
        this.showStatistics = true;

        this.viewTimeTicksMinutes = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'ViewTimeTicksMinutes'
        )[0]?.value;

        this.$interval(() => {
            this.displayCorrectButtons();
            this.configureSlido();
            this.renderStatistics();

            this.announcementsAttribute = this.announcementsOnly() ? 'yes' : 'no';
            this.questionDescriptiveText = this.event.userQuestionDescriptiveText;
            this.event = this.watchService.event;
        }, 1000);
    }

    configureSlido() {
        if (this.event.enableSlido) {
            if (this.event.slidoEventUrl === this.slidoUrl) {
                return;
            }

            let slidoUrl = this.event.slidoEventUrl;
            if (
                slidoUrl &&
                this.event.prefillSlidoCredentials &&
                this.user &&
                this.user._dn &&
                this.user._au
            ) {
                let name = this.user._dn;
                let email = this.user._au;
                let company = this.user._wk;
                if (!company) {
                    if (this.user._au.toLowerCase().indexOf('@volvocars.com') > 0) {
                        company = 'Volvo Car Corporation';
                    } else {
                        company = 'Unknown';
                    }
                }
                if (slidoUrl.indexOf('?') > 0) {
                    slidoUrl += '&';
                } else {
                    slidoUrl += '?';
                }
                slidoUrl +=
                    'user_name=' + name + '&user_email=' + email + '&user_company=' + company;
            }
            this.slidoUrl = this.$sce.trustAsResourceUrl(slidoUrl);
        } else {
            this.slidoUrl = '';
        }
    }

    customizeBtnOnClick() {
        if (this.shareButton !== null)
            this.shareButton.additionalOnClick = function (ctrl) {
                ctrl.getShareLinks();
            };

        if (this.chaptersButton !== null)
            this.chaptersButton.additionalOnClick = function (ctrl) {
                ctrl.getChapters();
            };

        if (this.calButton !== null)
            this.calButton.additionalOnClick = function (ctrl) {
                ctrl.getCalendarLinks();
            };
    }

    displayCorrectButtons() {
        this.displayCTABtn = this.showCTA();

        this.moreButton = this.getButton(BUTTON_TYPE.MoreMenu);

        // In all cases...
        this.adminButton = this.getButton(BUTTON_TYPE.Admin);
        if (this.adminButton !== null) this.adminButton.isAvailable = this.showAdminButton();

        this.shareButton = this.getButton(BUTTON_TYPE.Share);
        if (this.shareButton !== null) this.shareButton.isAvailable = this.showShareButton();

        this.statisticsButton = this.getButton(BUTTON_TYPE.Statistics);
        if (this.statisticsButton !== null)
            this.statisticsButton.isAvailable = this.showStatisticsButton();

        // During countdown...
        this.lqButton = this.getButton(BUTTON_TYPE.LobbyQuestion);
        if (this.lqButton !== null) this.lqButton.isAvailable = this.showQuestionnaireButton();

        this.calButton = this.getButton(BUTTON_TYPE.AddToCalendar);
        if (this.calButton !== null) this.calButton.isAvailable = this.showCalendarButton();

        // While watching webcast
        this.commentsButton = this.getButton(BUTTON_TYPE.Comments);
        if (this.commentsButton !== null) {
            this.commentsButton.isAvailable = this.showCommentsButton();
            this.commentsButton.title = this.announcementsOnly() ? 'Announcements' : 'Comments';
        }

        this.qnaButton = this.getButton(BUTTON_TYPE.Questions);
        if (this.qnaButton !== null) this.qnaButton.isAvailable = this.showQnAButton();

        this.embedButton = this.getButton(BUTTON_TYPE.Embed);
        if (this.embedButton !== null) this.embedButton.isAvailable = this.showEmbedButton();

        // Hack to set a dynamic header for the Slido tab
        if (this.event.slidoTabHeader) {
            this.embedButton.title = this.event.slidoTabHeader;
        }

        // After webcast + VoD
        this.chaptersButton = this.getButton(BUTTON_TYPE.Chapters);
        if (this.chaptersButton !== null)
            this.chaptersButton.isAvailable = this.showChaptersButton();

        this.manageAdminContent();
        this.setupToolbar();
    }

    setupToolbar() {
        this.numAvailableButtons = this.buttons.filter((btn) => {
            return btn.isAvailable && btn.isStandardButton;
        }).length;

        this.hasTabs = this.numAvailableButtons > 0;

        if (this.hasTabs) {
            let screenSize = this.getViewportWidthRange();

            switch (screenSize) {
                case 'XS':
                    this.maxNumToolbarButtons = 2;
                    break;
                case 'S':
                    this.maxNumToolbarButtons = 3;
                    break;
                case 'M':
                    this.maxNumToolbarButtons = 4;
                    break;
                case 'L':
                    this.maxNumToolbarButtons = 4; // Toolbar is too small for more buttons in countdown mode
                    break;
                default:
                // code block
            }

            if (this.maxNumToolbarButtons < this.numAvailableButtons) {
                this.moreMenuStartIndex = this.maxNumToolbarButtons;
                if (this.moreButton !== null) this.moreButton.isAvailable = true;
            } else {
                this.moreMenuStartIndex = -1;
                if (this.moreButton !== null) this.moreButton.isAvailable = false;
            }
        }
        if (this.embedButton && this.firstLoad) {
            this.firstLoad = false;
            this.toggleActiveContent(BUTTON_TYPE.Embed);
        }
    }

    manageAdminContent() {
        if (this.adminButton !== null && this.adminButton.isAvailable) {
            this.showPreviewToggler = true;
            if (this.watchService.countdownIsVisible()) {
                this.togglePreviewTitle = 'View webcast';
                this.previewAction = 'webcast';
            } else {
                this.togglePreviewTitle = 'View countdown';
                this.previewAction = 'countdown';
            }
        }
        this.showQnaModerationLink =
            userPerms.allowedToSeeEventAdminLinks(this.user, this.watchService.eAdms) &&
            (this.event.showQuestionnaire ||
                this.event.userInteractionType === USER_INTERACTION_TYPE.qna ||
                this.event.userInteractionType === USER_INTERACTION_TYPE.all ||
                this.event.userInteractionType === USER_INTERACTION_TYPE.qna_standalone);

        this.showEmbedLink =
            !this.event.eventAccessType.requireInvite &&
            userPerms.allowedToSeeEventAdminLinks(this.user, this.watchService.eAdms) &&
            this.event.shareContent['EmbedHtml'] !== undefined &&
            this.event.shareContent['EmbedHtml'].length > 0;
    }

    toggleActiveContent(buttonType) {
        let btn = this.getButton(buttonType);
        if (!btn.isAvailable) return;

        if (btn.isActive) btn.isActive = false;
        else {
            if (btn.additionalOnClick !== null) btn.additionalOnClick(this);
            btn.isActive = true;
            btn.hasNotification = false;
            localStorage.removeItem('newComment');
            let date = new Date();
            localStorage.removeItem('toggledTab');
            localStorage.setItem('toggledTab', date);

            this.buttons
                .filter((btn) => {
                    return btn.type !== buttonType;
                })
                .forEach((btn) => {
                    btn.isActive = false;
                });
        }
    }

    announcementsOnly() {
        return (
            (this.event.userInteractionType === USER_INTERACTION_TYPE.announcements ||
                this.event.userInteractionType === USER_INTERACTION_TYPE.qna) &&
            this.event.voDFirstPublished === null
        );
    }

    showAdminButton() {
        return userPerms.hasElevatedEventPermissions(this.user, this.watchService.eAdms);
    }

    showQnAButton() {
        return (
            this.watchService.videoIsVisible() &&
            this.event.voDFirstPublished === null &&
            (this.event.userInteractionType === USER_INTERACTION_TYPE.qna ||
                this.event.userInteractionType === USER_INTERACTION_TYPE.all ||
                this.event.userInteractionType === USER_INTERACTION_TYPE.qna_standalone)
        );
    }

    showEmbedButton() {
        return (
            this.event.enableSlido &&
            this.event.slidoEventUrl &&
            ((this.watchService.currentView === this.watchService.CURRENT_VIEW.countdown &&
                this.event.showSlidoBeforeWebcast) ||
                (this.watchService.currentView === this.watchService.CURRENT_VIEW.video &&
                    !utils.hasEventGracePeriodEnded(this.event) &&
                    this.event.showSlidoDuringWebcast) ||
                (this.watchService.currentView === this.watchService.CURRENT_VIEW.video &&
                    utils.hasEventGracePeriodEnded(this.event) &&
                    this.event.showSlidoAfterWebcast))
        );
    }

    showChaptersButton() {
        return (
            this.watchService.videoIsVisible() &&
            this.event.viewingState === VIEWING_STATE.published &&
            this.event.voDChaptersFilePath !== undefined &&
            this.event.voDChaptersFilePath !== null
        );
    }

    showShareButton() {
        // Only if event is completely open
        return this.event.eventAccessType.id === 1;
    }

    showStatisticsButton() {
        return this.watchService.currentView === this.watchService.CURRENT_VIEW.video &&
          userPerms.hasElevatedEventPermissions(this.user, this.watchService.eAdms);
    }

    showCommentsButton() {
        return (
            this.watchService.videoIsVisible() &&
            (this.unpublishedWithAnnouncements() || this.publishedWithVoDComments())
        );
    }

    showQuestionnaireButton() {
        let now = new Date();
        if (
            this.watchService.countdownIsVisible() &&
            this.event.showQuestionnaire &&
            this.event.questionnaireShowStart !== null &&
            this.event.questionnaireShowEnd !== null
        ) {
            let msSinceQStart = differenceInMilliseconds(
                now,
                utils.getDateFromUTCString(this.event.questionnaireShowStart)
            );
            let msBeforeQEnd = differenceInMilliseconds(
                utils.getDateFromUTCString(this.event.questionnaireShowEnd),
                now
            );
            return msSinceQStart > 0 && msBeforeQEnd > 0;
        }
        return false;
    }

    showCalendarButton() {
        return this.watchService.countdownIsVisible() === true;
    }

    showCTA() {
        return (
            this.watchService.videoIsVisible() &&
            this.event.enableCTAButton &&
            (this.event.voDFirstPublished === null || this.event.showCTAButtonInVod)
        );
    }

    unpublishedWithAnnouncements() {
        return (
            this.event.userInteractionType !== USER_INTERACTION_TYPE.none &&
            this.event.userInteractionType !== USER_INTERACTION_TYPE.qna_standalone &&
            this.event.voDFirstPublished === null
        );
    }

    publishedWithVoDComments() {
        return this.event.enableVoDComments && this.event.voDFirstPublished !== null;
    }

    addNotification(buttonType) {
        this.buttons
            .filter((btn) => {
                return (
                    btn.type === buttonType && btn.isActive === false && btn.isAvailable === true
                );
            })
            .forEach((btn) => {
                let toggledTab = localStorage.getItem('toggledTab');
                let lastComment = localStorage.getItem('newComment');
                if (lastComment !== null && toggledTab === null) {
                    btn.hasNotification = true;
                } else if (new Date(lastComment) >= new Date(toggledTab)) {
                    btn.hasNotification = true;
                } else btn.hasNotification = false;
            });
    }

    getCalendarLinks() {
        this.calendarLinks = addToCalendarData({
            data: {
                title: this.event.titleCombined,
                start: utils.getDateFromUTCString(this.event.startTime),
                end: utils.getDateFromUTCString(this.event.stopTime),
                address: 'live.volvocars.com',
                description: 'Watch the webcast here: ' + this.event.directUrl,
                friendlyUrlPath: this.event.friendlyUrlPath,
            },
        });
    }

    calendarDownloadFallback(calObj) {
        if (calObj !== undefined && calObj !== null) ieDownloadCalendar(calObj);
    }

    getShareLinks() {
        let linksToShow = [];
        let generatedLinks = this.watchService.event.shareContent;

        // Show social media icons only if this webcast is open for everyone
        if (this.watchService.event.eventAccessType.id === 1) {
            if (
                generatedLinks['FacebookUrl'] !== undefined &&
                generatedLinks['FacebookUrl'].length > 0
            )
                linksToShow.push({
                    href: generatedLinks['FacebookUrl'],
                    name: 'Facebook',
                    icon: 'facebook',
                });
            if (
                generatedLinks['LinkedInUrl'] !== undefined &&
                generatedLinks['LinkedInUrl'].length > 0
            )
                linksToShow.push({
                    href: generatedLinks['LinkedInUrl'],
                    name: 'LinkedIn',
                    icon: 'linkedin',
                });
            if (
                generatedLinks['TwitterUrl'] !== undefined &&
                generatedLinks['TwitterUrl'].length > 0
            )
                linksToShow.push({
                    href: generatedLinks['TwitterUrl'],
                    name: 'Twitter',
                    icon: 'twitter',
                });
        }

        linksToShow.push({
            stringToCopy: this.watchService.event.directUrl,
            name: 'Send link',
            icon: 'email',
        });
        this.shareLinks = linksToShow;
    }

    roundUpToNearest(num) {
        const digits = Math.floor(Math.log10(num)) + 1;
        let nearest;

        if (digits <= 2) {
            nearest = 10;
        } else if (digits <= 3) {
            nearest = 100;
        } else {
            nearest = 10 ** (digits - 1);
        }

        return Math.ceil(num / nearest) * nearest;
    }

    renderViewsOverTime() {
        if (!this.showStatistics) return;
        if (this.viewsOverTimeChart) this.viewsOverTimeChart.destroy();

        // Styling based upon screen size and organization
        const largeScreenMatch = window.matchMedia("(min-width: 992px)");
        const isPolestar = siteSettings.currentOrgName === 'Polestar';
        Chart.defaults.font.size = largeScreenMatch.matches ? 14 : 12;
        Chart.defaults.font.family = isPolestar ?
            'Polestar Regular, Arial, Helvetica, sans-serif' :
            'Volvo Novum Light, Volvo Novum Medium, Volvo Novum Regular, sans-serif';
        const borderColor = isPolestar ? '#101820' : '#3D5881';
        const fontColor = isPolestar ? '#101820' : '#707070';

        // Add a zero value to the beginning of the views over time object
        // and increment the following values to make sure the chart starts at 0
        const keyValuePairs = [{ 0: 0 }, ...Object.keys(this.statistics.ViewsOverTime).map((interval) => {
            const newTick = parseInt(interval) + parseInt(this.viewTimeTicksMinutes);
            const newEntity = { [newTick]: this.statistics.ViewsOverTime[interval] };
            return newEntity;
        })];
        const viewsOverTime = Object.assign({}, ...keyValuePairs);

        const labels = Object.keys(viewsOverTime).map((interval) => {
            const hours = Math.floor(interval / 60);
            const minutes = interval % 60;
            const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes.toString();
            return `${hours}:${formattedMinutes}`;
        });
        const values = Object.values(viewsOverTime);
        const maxYValue = this.roundUpToNearest(Math.max.apply(null, values));

        const canvas = document.getElementById('vl-views-over-time');
        this.viewsOverTimeChart = new Chart(canvas, {
            type: 'line',
            data: {
                labels: labels,
                datasets: [
                    {
                        data: values,
                        tension: 0.3,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                },
                scales: {
                    x: {
                        title: {
                            text: 'Views over time (hour:minute)',
                            display: true,
                        },
                        ticks: {
                            maxTicksLimit: 5,
                        },
                        grid: {
                            display: false,
                        },
                    },
                    y: {
                        ticks: {
                            maxTicksLimit: 5,
                        },
                        min: 0,
                        max: maxYValue,
                        grid: {
                            display: false,
                        },
                    },
                },
                borderColor: borderColor,
                color: fontColor,
            },
        });
    }

    renderStatistics() {
        if (this.watchService.currentView !== this.watchService.CURRENT_VIEW.video)
            return;

        // Get statistics from different sources depending on the viewing state
        let newStats;
        if (this.watchService.event.viewingState <= 1 && this.watchService.liveStats) {
            newStats = this.watchService.liveStats;
        } else if (this.watchService.event.statsMinimal && this.watchService.event.statsMinimal !== '') {
            newStats = JSON.parse(this.watchService.event.statsMinimal);
        }

        // Hack: Chart.js doesn't like the conditional Angular rendering
        const votDiv = document.getElementsByClassName('vl-views-over-time-container');
        if (!newStats) {
            this.showStatistics = false;
            if (votDiv && votDiv.length > 0) votDiv[0].style.display = 'none';
              return;
        } else {
            this.showStatistics = true;
            if (votDiv && votDiv.length > 0) votDiv[0].style.display = 'block';
        }

        // Don't re-render the statistics if the data hasn't changed
        if (JSON.stringify(this.statistics) === JSON.stringify(newStats)) {
            return;
        }
        else {
            this.statistics = newStats;
        }

        // Total views
        if (this.watchService.event.viewingState <= 1) {
            this.totalViewsTitle = 'Watching now';
            this.totalViews = this.statistics.OnlineViewers.toLocaleString();
        } else {
            this.totalViewsTitle = 'Total views';
            this.totalViews = this.statistics.TotalViewCount.toLocaleString();
        }

        // Views over time
        this.renderViewsOverTime();

        // Top countries
        isoCountries.registerLocale(en);
        this.topCountries = this.statistics.TopCountries.map((country) => {
            const name = isoCountries.getName(country.Name, 'en');
            return {
                name: name,
                views: country.NoOfViews.toLocaleString(),
                class: 'fi fi-' + country.Name.toLowerCase(),
            };
        });

        // Top cities
        this.topCities = this.statistics.TopCities.map((city) => {
            const country = city.Name.substring(0, city.Name.indexOf('|')).toLowerCase();
            let cityName = city.Name.substring(city.Name.indexOf('|') + 1, city.Name.length);
            return {
                name: cityName,
                views: city.NoOfViews.toLocaleString(),
                class: 'fi fi-' + country,
            };
        });
    }

    displayShareLink(textToShow) {
        if (this.contentToCopy === textToShow) this.contentToCopy = undefined;
        // Hides the input field
        else this.contentToCopy = textToShow;
    }

    copyText(elemId) {
        let textarea = document.querySelector(`#${elemId} textarea`);
        if (textarea !== undefined) {
            textarea.select();
            document.execCommand('copy');
        }
    }

    getChapters() {
        if (this.chapters !== undefined && this.chapters !== null) return;

        var vjsPlayer = utils.getVJSPlayer(videoJS);
        var tracks = vjsPlayer.textTracks();
        let chapterTrack = null;

        for (var i = 0; i < tracks.length; i++) {
            var track = tracks[i];
            if (track.kind === 'chapters') {
                chapterTrack = track;
                break;
            }
        }

        this.chapters = chapterTrack;
    }

    goToChapter(cue) {
        var vjsPlayer = utils.getVJSPlayer(videoJS);
        vjsPlayer.currentTime(cue.startTime);
        vjsPlayer.play();
        this.toggleChaptersView();
    }

    isChapterActive(cue) {
        var vjsPlayer = utils.getVJSPlayer(videoJS);
        let cTime = vjsPlayer.currentTime();
        return cTime >= cue.startTime && cTime < cue.endTime;
    }

    zeroPad(num) {
        return num.toString().replace(/^([0-9]{1,1}$)/, '0$1');
    }

    chapterStart(cue) {
        let msFromStart = cue.startTime * 1000;
        let duration = intervalToDuration({ start: 0, end: msFromStart });
        return `${this.zeroPad(duration.hours)}:${this.zeroPad(duration.minutes)}:${this.zeroPad(
            duration.seconds
        )}`;
    }

    getButton(type) {
        let btnsOfType = this.buttons.filter((btn) => {
            return btn.type === type;
        });
        if (btnsOfType.length > 0) return btnsOfType[0];

        return null;
    }

    activeButtonsFilter(button) {
        return button.isAvailable && button.isStandardButton;
    }

    getViewportWidthRange() {
        let width = 0;
        if (window.innerWidth) {
            width = window.innerWidth;
        } else if (document.body && document.body.offsetWidth) {
            width = document.body.offsetWidth;
        }

        if (width < 500) return 'XS';

        if (width < 700) return 'S';

        if (width < 1200) return 'M';

        return 'L';
    }
}
